/**
 * Copyright (c) Siemens 2016 - 2026
 * SPDX-License-Identifier: MIT
 */
import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterContentInit,
  Component,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import { elementCalendar } from '@siemens/element-icons';
import { addIcons, SiIconComponent } from '@siemens/element-ng/icon';
import { SiTranslatePipe, t } from '@siemens/element-translate-ng/translate';
import { filter } from 'rxjs';

import { SiDatepickerOverlayDirective } from './si-datepicker-overlay.directive';
import { SiDatepickerDirective } from './si-datepicker.directive';

/**
 * Calendar toggle button used in combination with a datepicker directive.
 *
 * @example
 * ```
 * <si-calendar-button class="w-100">
 *   <input
 *     class="form-control"
 *     type="text"
 *     siDatepicker
 *     [siDatepickerConfig]="config"
 *   />
 * </si-calendar-button>
 * ```
 */
@Component({
  selector: 'si-calendar-button',
  imports: [SiIconComponent, SiTranslatePipe],
  template: `<ng-content />
    <div class="form-control-actions">
      <button
        #calendarButton
        name="open-calendar"
        type="button"
        class="btn btn-icon btn-tertiary btn-sm"
        [attr.aria-label]="ariaLabel() | translate"
        [disabled]="disabled()"
        (click)="show()"
      >
        <si-icon [icon]="icons.elementCalendar" />
      </button>
    </div>`,
  styleUrl: './si-calendar-button.component.scss',
  host: {
    class: 'd-inline-block position-relative form-control-wrapper'
  }
})
export class SiCalendarButtonComponent implements OnInit, AfterContentInit {
  /**
   * Aria label for the calendar toggle button.
   *
   * @defaultValue
   * ```
   * t(() => $localize`:@@SI_DATEPICKER.CALENDAR_TOGGLE_BUTTON:Open calendar`)
   * ```
   */
  readonly ariaLabel = input(
    t(() => $localize`:@@SI_DATEPICKER.CALENDAR_TOGGLE_BUTTON:Open calendar`)
  );

  protected readonly button = viewChild.required<ElementRef<HTMLButtonElement>>('calendarButton');
  /** Datepicker input directive instance used to watch for state changes and required to open the calendar. */
  protected readonly datepicker = contentChild.required(SiDatepickerDirective);
  protected readonly datepickerOverlay = contentChild.required(SiDatepickerOverlayDirective);
  protected readonly ngControl = contentChild(NgControl);
  protected readonly disabled = signal(false);
  protected readonly icons = addIcons({ elementCalendar });
  private readonly destroyerRef = inject(DestroyRef);
  private readonly focusMonitor = inject(FocusMonitor);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  ngOnInit(): void {
    // Monitor input state changes and update the button accordingly
    this.datepicker().stateChange.subscribe(() => this.updateState());
    this.focusMonitor
      .monitor(this.elementRef, true)
      .pipe(
        takeUntilDestroyed(this.destroyerRef),
        filter(origin => origin === null)
      )
      .subscribe(() => {
        if (!this.datepickerOverlay().isShown()) {
          this.datepicker().touch();
        }
      });
  }

  ngAfterContentInit(): void {
    this.datepicker().useExternalTrigger(this.button());
    this.updateState();
  }

  protected show(): void {
    this.datepicker().show(true);
  }

  private updateState(): void {
    const datepicker = this.datepicker();
    this.disabled.set(datepicker.disabled() || datepicker.readonly());
  }
}
